import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Home,
  MessageSquare,
  Briefcase,
  Calendar,
  Award,
  ChevronRight,
  Columns,
  UserPlus,
  Building2,
  Key
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/use-toast';
import logo from '../../assets/branding/logo-short.png';

const AdminLayout = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const pathname = location.pathname;

  const categories = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', href: '/admin/dashboard' },
    {
      id: 'crm', icon: Users, label: 'CRM', subItems: [
        { name: 'Leads', href: '/admin/leads', icon: Users },
        { name: 'Schedules', href: '/admin/schedules', icon: Calendar },
      ]
    },
    {
      id: 'content', icon: FileText, label: 'Content', subItems: [
        { name: 'Projects', href: '/admin/projects', icon: Briefcase },
        { name: 'Case Studies', href: '/admin/case-studies', icon: FileText },
        { name: 'Testimonials', href: '/admin/testimonials', icon: MessageSquare },
      ]
    },
    {
      id: 'employee_mgmt', icon: Building2, label: 'Employees', subItems: [
        { name: 'Employee Manager', href: '/admin/employee-manager', icon: UserPlus },
        { name: 'Task Board', href: '/admin/kanban', icon: Columns },
        { name: 'Employee Records', href: '/admin/employeeships', icon: Award },
      ]
    },
    {
      id: 'system', icon: Settings, label: 'System', subItems: [
        { name: 'Settings', href: '/admin/settings', icon: Settings },
        { name: 'API Keys', href: '/admin/api-keys', icon: Key },
      ]
    },
  ];

  // Derive active category from current URL
  const activeCategory = categories.find(cat =>
    cat.href === pathname || cat.subItems?.some(sub => sub.href === pathname)
  )?.id || 'dashboard';

  const currentCategory = categories.find(c => c.id === activeCategory) || categories[0];
  const activeSubItem = currentCategory.subItems?.find(sub => sub.href === pathname);

  React.useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate({ to: '/admin/login' });
      } else if (pathname === '/admin' || pathname === '/admin/') {
        navigate({ to: '/admin/dashboard' });
      }
    }
  }, [isAuthenticated, loading, navigate, pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#1e1f22] text-[#dbdee1] flex overflow-hidden font-sans">
      {/* 🔴 Left Rail (Discord-style icons) */}
      <aside className="w-[72px] bg-[#1e1f22] flex flex-col items-center py-3 space-y-2 z-50 flex-shrink-0">
        <div
          onClick={() => navigate({ to: '/' })}
          className="w-12 h-12 bg-white/5 hover:bg-white/10 text-silver rounded-[12px] transition-all duration-200 flex items-center justify-center mb-2 cursor-pointer group shadow-lg overflow-hidden"
          title="Go to Website"
        >
          <img src={logo} alt="HARA-XY" className="w-8 h-8 object-contain" />
          <div className="absolute left-0 w-1 h-8 bg-white rounded-r-lg opacity-0 -translate-x-full transition-all group-hover:opacity-100 group-hover:translate-x-0"></div>
        </div>

        <div className="w-8 h-[2px] bg-white/5 mb-2 rounded-full"></div>

        {categories.map((cat) => (
          <div key={cat.id} className="relative group">
            <button
              onClick={() => {
                const target = cat.href || (cat.subItems && cat.subItems[0].href);
                if (target) navigate({ to: target });
              }}
              className={`w-12 h-12 flex items-center justify-center transition-all duration-200 relative ${activeCategory === cat.id
                ? 'bg-silver text-black rounded-[12px]'
                : 'bg-[#313338] text-gray-400 hover:bg-silver hover:text-black rounded-[24px] hover:rounded-[12px]'
                }`}
            >
              <cat.icon size={24} />
            </button>
            {/* Active Indicator */}
            {activeCategory === cat.id && (
              <div className="absolute -left-[12px] top-1/2 -translate-y-1/2 w-2 h-10 bg-white rounded-r-lg"></div>
            )}
            {!(activeCategory === cat.id) && (
              <div className="absolute -left-[12px] top-1/2 -translate-y-1/2 w-2 h-0 bg-white rounded-r-lg group-hover:h-5 transition-all"></div>
            )}

            {/* Tooltip */}
            <div className="absolute left-16 top-1/2 -translate-y-1/2 bg-black text-white text-[12px] font-bold px-3 py-1.5 rounded shadow-xl opacity-0 scale-90 pointer-events-none group-hover:opacity-100 group-hover:scale-100 transition-all origin-left whitespace-nowrap z-[100]">
              {cat.label}
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-black rotate-45"></div>
            </div>
          </div>
        ))}

        <div className="mt-auto pb-4">
          <button
            onClick={handleLogout}
            className="w-12 h-12 bg-[#313338] text-red-500 rounded-[24px] hover:rounded-[12px] hover:bg-red-500 hover:text-white transition-all duration-200 flex items-center justify-center group"
            title="Logout"
          >
            <LogOut size={22} />
          </button>
        </div>
      </aside>

      {/* 🟠 Middle Navigation Pane */}
      <nav className={`
        w-60 bg-[#2b2d31] flex flex-col flex-shrink-0 border-r border-black/10
        ${!currentCategory.subItems ? 'hidden' : ''}
      `}>
        <div className="h-12 flex items-center px-4 shadow-sm border-b border-[#1e1f22] cursor-pointer hover:bg-[#35373c] transition-colors">
          <h2 className="font-bold text-[#f2f3f5] text-[15px] truncate flex-1">
            {currentCategory.label}
          </h2>
          <ChevronRight size={18} className="text-[#949ba4] rotate-90 opacity-80" />
        </div>

        <div className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto custom-scrollbar">
          {currentCategory.subItems?.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2 w-full rounded-md transition-colors group ${
                pathname === item.href
                  ? 'bg-[#404249] text-[#dbdee1]'
                  : 'text-[#949ba4] hover:bg-[#35373c] hover:text-[#dbdee1]'
              }`}
            >
              <item.icon className={`h-[18px] w-[18px] flex-shrink-0 ${pathname === item.href ? 'text-white' : 'opacity-80 group-hover:opacity-100 group-hover:text-[#dbdee1]'} transition-colors`} />
              <span className={`text-[15px] font-medium leading-none truncate ${pathname === item.href ? 'font-semibold text-white' : ''}`}>{item.name}</span>
            </Link>
          ))}
        </div>

        {/* User Status Bar */}
        <div className="p-1 px-[4px] bg-[#232428] flex items-center justify-between gap-1 flex-shrink-0 h-[52px]">
          <div className="flex items-center gap-2 min-w-0 p-[4px] rounded hover:bg-white/10 transition-colors cursor-pointer flex-1">
            <div className="relative flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-silver flex items-center justify-center font-bold text-black text-xs">
                {user?.name?.[0] || 'A'}
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-[#232428] rounded-full"></div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold text-white truncate leading-tight">{user?.name}</div>
              <div className="text-[11px] text-[#949ba4] truncate">Online</div>
            </div>
          </div>
          <button
            onClick={() => navigate({ to: '/admin/settings' })}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors flex-shrink-0"
            title="Settings"
          >
            <Settings size={18} />
          </button>
        </div>
      </nav>

      {/* ⚪ Content Area */}
      <div className="flex-1 flex flex-col bg-[#313338] min-w-0">
        {/* Header Strip */}
        <header className="h-12 bg-[#313338] px-4 flex items-center shadow-md z-1 relative border-b border-black/10">
          <div className="flex items-center gap-2 text-[#949ba4]">
            <currentCategory.icon size={20} className="opacity-40" />
            <span className="font-bold text-white text-[15px] ml-1">{currentCategory.label}</span>
            {activeSubItem && (
              <>
                <ChevronRight size={14} className="opacity-40" />
                <span className="text-[14px] font-semibold text-[#dbdee1]">
                  {activeSubItem.name}
                </span>
              </>
            )}
          </div>
        </header>

        {/* Main Viewport */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-[#313338]">
          <div className="p-6 md:p-8 animate-in fade-in duration-300">
            <Outlet />
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #1e1f22;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #151618;
        }
        @import url('https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700;800&display=swap');
        .App, body {
           font-family: 'Public Sans', sans-serif !important;
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;